import { MutationFunction, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import axios, { AxiosError, AxiosResponse } from 'axios'
import { Cookies } from 'react-cookie'
import { IPriceInfo } from '../../../components/student/portal/pricing/PriceInfo'
import { IProductDetail } from '../../../model/product.interface'
import {
  IOnboardingVideos,
  ITrial,
  ITrialCourses,
  ITrialPoints,
  ITrialSchedules,
  ITrialSummary,
  ITrialTutors,
  LessonDurationType,
} from '../../../model/trial.interface'
import apis from '../../../modules/apis'
import { LocaleType } from '../../../modules/i18n/config'
import { getJwtToken } from '../../auth'
import { axiosPost } from '../../axiosHelper'
import useTranslation from '../../i18n/useTranslation'
import {
  trialCoursesQueryApi,
  trialOnboardingVideosQueryApi,
  trialProductDetailQueryApi,
  trialSchedulesQueryApi,
  trialStatusQueryApi,
  trialStatusSummaryQueryApi,
  trialTutorsueryApi,
} from '../../react-query/queryApis'
import { COOKIE_LIST } from '../../vars'
import { createAxiosTokenConfig, handleAxiosError } from '../axios-util'

export const useTrialStatusSummary = () => {
  const cookies = new Cookies()
  const token = cookies.get(COOKIE_LIST.JWT_TOKEN)

  return useQuery<ITrialSummary, AxiosError>({
    queryKey: trialStatusSummaryQueryApi.getQueryKey(),
    queryFn: trialStatusSummaryQueryApi.getQueryFn(),
    // onError: (e) => handleAxiosError(e),
    enabled: !!token,
  })
}

export const useTrialOnboardingVideos = (locale: LocaleType) => {
  const searchParams = { locale }

  return useQuery<IOnboardingVideos, AxiosError>({
    queryKey: trialOnboardingVideosQueryApi.getQueryKey({ searchParams }),
    queryFn: trialOnboardingVideosQueryApi.getQueryFn({ searchParams }),

    // onError: (e) => handleAxiosError(e),
    cacheTime: 1000 * 30,
    staleTime: 1000 * 30,
  })
}

export const useCompleteOnboardingVideos = () => {
  const queryClient = useQueryClient()
  const { locale } = useTranslation()
  return useMutation({
    mutationFn: () => {
      return axiosPost(apis.trial.onboardingVideos.complete())
    },
    onSettled: () => {
      const searchParams = { locale, duration: 20 }
      queryClient.invalidateQueries(trialStatusQueryApi.getQueryKey({ searchParams }))
    },
  })
}
interface useUpdateOnboardingVideoIdPayload {
  completed: boolean
}

interface useUpdateOnboardingVideoIdResponse {
  success: boolean
}

export const useUpdateOnboardingVideoId = ({ videoId }) => {
  const { locale } = useTranslation()
  const queryClient = useQueryClient()
  const token = getJwtToken()

  const postUrl = apis.trial.onboardingVideos.update(videoId)
  const searchParams = { locale }
  const queryKey = trialOnboardingVideosQueryApi.getQueryKey({ searchParams })

  return useMutation<
    AxiosResponse<useUpdateOnboardingVideoIdResponse>,
    AxiosError,
    useUpdateOnboardingVideoIdPayload,
    { prev: IOnboardingVideos }
  >({
    mutationFn: (payload) =>
      axios.post<useUpdateOnboardingVideoIdResponse>(postUrl, payload, createAxiosTokenConfig(token)),

    onMutate: async (payload) => {
      await queryClient.cancelQueries(queryKey)

      const prev = queryClient.getQueryData<IOnboardingVideos>(queryKey)

      queryClient.setQueryData<IOnboardingVideos>(queryKey, (prevData) => {
        prevData.onboarding_videos.map((video) => {
          if (video.id === videoId) {
            return {
              ...video,
              completed: true, // payload
            }
          }
        })
        return prevData
      })

      return { prev }
    },

    onError: (error, payload, context) => {
      const { prev } = context
      console.log('MUTATION ERROR :: ', error, error.response)

      queryClient.setQueryData<IOnboardingVideos>(queryKey, prev)
    },

    onSuccess: (response, payload, context) => {
      const { data } = response
      console.log('MUTATION SUCCESS ::', data)
    },

    onSettled: (response, error, payload, context) => {
      console.log('QUERY INVALIDATE ::', queryKey)
      queryClient.invalidateQueries(queryKey)

      if (error) handleAxiosError(error)
    },
  })
}

interface useTrialSchedulesParams {
  duration: LessonDurationType
}

export const useTrialSchedules = ({ duration }: useTrialSchedulesParams) => {
  const cookies = new Cookies()
  const token = cookies.get(COOKIE_LIST.JWT_TOKEN)
  const searchParams = { duration }
  return useQuery<ITrialSchedules, AxiosError>({
    queryKey: trialSchedulesQueryApi.getQueryKey({ searchParams }),
    queryFn: trialSchedulesQueryApi.getQueryFn({ searchParams }),

    // onError: (e) => handleAxiosError(e),
    retry: false,
    enabled: !!token,
  })
}

interface useTrialTutorsParams {
  classtimeId: number
  duration: LessonDurationType
}

export const useTrialTutors = ({ classtimeId, duration }: useTrialTutorsParams) => {
  const searchParams = { classtime_id: classtimeId, duration }

  return useQuery<ITrialTutors, AxiosError>({
    queryKey: trialTutorsueryApi.getQueryKey({ searchParams }),
    queryFn: trialTutorsueryApi.getQueryFn({ searchParams }),

    // onError: (e) => handleAxiosError(e),
  })
}

export interface useTrialBookPayload {
  classtime_id: number
  tutor_id: number
  duration: LessonDurationType
}

export const useTrialBook = <T>() => {
  const mutationFn: MutationFunction<AxiosResponse<T>, useTrialBookPayload> = async (payload) => {
    const token = getJwtToken()
    const { classtime_id, tutor_id, duration } = payload
    return await axios.post<T>(apis.trial.book(classtime_id, tutor_id, duration), undefined, {
      ...createAxiosTokenConfig(token),
    })
  }
  return useMutation<AxiosResponse<T>, AxiosError, useTrialBookPayload>({ mutationFn })
}

export const useTrialCourses = () => {
  return useQuery<ITrialCourses, AxiosError>({
    queryKey: trialCoursesQueryApi.getQueryKey(),
    queryFn: trialCoursesQueryApi.getQueryFn(),

    // onError: (e) => handleAxiosError(e),
  })
}

interface useTrialApplyPayload {
  classtime_id: number
  tutor_id: number
  course_id: number
  duration?: LessonDurationType
}

export const useTrialApply = () => {
  return useMutation({
    mutationFn: ({ classtime_id, tutor_id, course_id, duration }: useTrialApplyPayload) => {
      return axiosPost(apis.trial.apply(classtime_id, tutor_id, course_id, duration || 20))
    },
  })
}

export const useAgreeMktSetting = () => {
  return useMutation({
    mutationFn: () => {
      return axiosPost(apis.auth.notificationAllMktUpdate())
    },
  })
}

export const useTrialStatus = <T = ITrial>({
  locale,
  select,
  duration,
}: {
  locale: LocaleType
  select?: (data: any) => any
  duration?: LessonDurationType
}) => {
  const cookies = new Cookies()
  const token = cookies.get(COOKIE_LIST.JWT_TOKEN)

  const searchParams = { locale, duration }

  return useQuery<T, AxiosError>({
    queryKey: trialStatusQueryApi.getQueryKey({ searchParams }),
    queryFn: trialStatusQueryApi.getQueryFn({ searchParams }),
    refetchOnWindowFocus: true,
    // onError: (e) => handleAxiosError(e),
    enabled: !!token,
    select,
  })
}

// trial api의 특정 부분만 select 해서 가져오기 (useTrialPoints hook 추가 23.06.23)
const transformTrialData = (data: ITrial) => {
  const isReceivedVideoPoint = data?.received_points?.onboarding_videos?.completed
  const isReceivedProfilePoint = data?.received_points?.profile?.completed
  const videoPointAmount = data?.received_points?.onboarding_videos?.point_init_amount
  const profilePointAmount = data?.received_points?.profile?.point_init_amount

  const totalReceivedPoints =
    (isReceivedVideoPoint ? videoPointAmount : 0) + (isReceivedProfilePoint ? profilePointAmount : 0)

  return {
    isReceivedVideoPoint,
    isReceivedProfilePoint,
    videoPointAmount,
    profilePointAmount,
    totalReceivedPoints,
  }
}

export const useTrialPoints = () => {
  const { locale } = useTranslation()
  return useTrialStatus<ITrialPoints>({ locale, select: transformTrialData })
}

export const useTrialProductDetail = (isBefore24Hours: boolean) => {
  // dependant queries
  const { locale } = useTranslation()
  const { data } = useTrialStatus({ locale })
  const productId = isBefore24Hours
    ? data?.trial_product_lines?.trial_product_before_24_hours_id
    : data?.trial_product_lines?.trial_product_after_24_hours_id

  const pathParams = { productId }
  const searchParams = { country_code: 'KR' }

  return useQuery<IProductDetail, AxiosError, IPriceInfo>({
    queryKey: trialProductDetailQueryApi.getQueryKey({ pathParams, searchParams }),
    queryFn: trialProductDetailQueryApi.getQueryFn({ pathParams, searchParams }),

    // onError: (e) => handleAxiosError(e),
    enabled: !!productId,
    select: (data) => data.product_line,
    cacheTime: 1000 * 60 * 24,
    staleTime: 1000 * 60 * 24,
  })
}
